import {Queue} from './Queue';
import {DEFAULT_VOICE_INDEX, Output} from './Configuration';
import {
    AudioConfig,
    AudioOutputStream,
    SpeechConfig,
    SpeechSynthesisOutputFormat,
    SpeechSynthesizer
} from 'microsoft-cognitiveservices-speech-sdk';
import {createAudioContext} from './TextToSpeechUtilsPoly';
import {levenshtein} from './Levenshtein';

export class Text2SpeechProcessor {
    //Services
    private apiKey: string;
    private region = 'eastus';
    private speechSynthesizer: SpeechSynthesizer;

    //Config
    private outputConfig: Output;
    private readonly speechConfig: SpeechConfig;

    //Devices
    private audioContext: AudioContext | null;
    private currentGainNode: GainNode | null = null;
    private audioBufferSourceNode: AudioBufferSourceNode | null = null;

    //Prollers
    private pollingInterval: NodeJS.Timer | undefined = undefined;
    private speakerPollingInterval: NodeJS.Timer | undefined = undefined;

    //State
    private pendingText2SpeechQueue: Queue<string>;
    private pendingSpeakerQueue: Queue<AudioBuffer>;
    private imSpeaking: boolean = false;
    private currentRecognizedTokens: string[] | null = null;

    constructor(outputConfig: Output, azureSpeechAPIToken: string) {
        this.outputConfig = {...outputConfig};
        this.pendingText2SpeechQueue = new Queue<string>();
        this.pendingSpeakerQueue = new Queue<AudioBuffer>();

        this.apiKey = azureSpeechAPIToken;

        console.log('[Text2SpeechProcessor] Creating T2S processor...');
        this.speechConfig = SpeechConfig.fromAuthorizationToken(this.apiKey, this.region);
        /*this.speechConfig.speechSynthesisLanguage = outputConfig.language.speakKey;
        this.speechConfig.speechSynthesisVoiceName = outputConfig.language.voices[0];*/
        this.speechConfig.speechSynthesisOutputFormat = SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3

        this.audioContext = outputConfig.textOnly ? null : createAudioContext(outputConfig.device.deviceId) as AudioContext;

        //Not sure why; I think this disables default Azure behavior of playing back sound automatically on default device
        //We wont use this stream nor the AudioConfig either, but it is mandatory for creating a Synthesizer
        const pullAudioOutputStream = AudioOutputStream.createPullStream();
        const audioConfig = AudioConfig.fromStreamOutput(pullAudioOutputStream);

        this.speechSynthesizer = new SpeechSynthesizer(this.speechConfig, audioConfig);
        this.warmUpConnection();

        this.currentRecognizedTokens = [];

        console.log('[Text2SpeechProcessor] Ready and connected to Services and Devices');
    }

    private warmUpConnection() {
        console.log('[Text2SpeechProcessor] Warming up connection with dummy text...');
        this.speechSynthesizer.speakTextAsync('Testing...');
    }

    public setSpeechToken(newToken: string) {
        this.speechSynthesizer.authorizationToken = newToken;
    }

    /**
     * Add text to the processor queue.
     * A poller eventually will take this text and perform the following tasks:
     * - Add ssml to the text
     * - Send it to the Text2Speech and get a Speech audio back
     * - Enqueue in the Audio Output the audio
     * @param textToSpeak
     */
    public enqueueText = (textToSpeak: string): void => {
        console.log('[Text2SpeechProcessor] Queueing T2S services...', textToSpeak)
        this.pendingText2SpeechQueue.enqueue(textToSpeak);
    }

    /**
     * Enqueue an AudioBuffer to the connected device.
     * A poller will periodically consume this queue and play the audio on the device.
     * @param audioToSpeak
     */
    public sendToSpeakerQueue = (audioToSpeak: AudioBuffer): void => {
        console.log('[Text2SpeechProcessor] Queueing audio to device...', audioToSpeak)
        this.pendingSpeakerQueue.enqueue(audioToSpeak);
    }

    /**
     *
     */
    public start(): void {
        if (this.pollingInterval || this.speakerPollingInterval) {
            throw new Error('[Text2SpeechProcessor] Cant start Text2Speech processor. There\'s already a polling interval running.');
        }

        this.pollingInterval = setInterval(() => {
            if (this.imSpeaking)
                return;

            // console.log('[Text2SpeechProcessor] dequeing... Queue', this.pendingText2SpeechQueue);
            let textToSpeak = this.pendingText2SpeechQueue.dequeue();
            // console.log('[Text2SpeechProcessor] dequeued', textToSpeak);
            if (!!textToSpeak)
                this.speak(textToSpeak);

        }, 100)

        this.speakerPollingInterval = setInterval(() => {
            if (this.imSpeaking)
                return;

            // console.log('[Text2SpeechProcessor] dequeing... Queue', this.pendingText2SpeechQueue);
            let audioToSpeak = this.pendingSpeakerQueue.dequeue();
            // console.log('[Text2SpeechProcessor] dequeued', textToSpeak);
            if (!!audioToSpeak)
                this.playOnDevice(audioToSpeak);

        }, 100)
    }

    public stop(): void {
        clearInterval(this.pollingInterval);
        clearInterval(this.speakerPollingInterval);
        this.speechSynthesizer.close();
        this.currentGainNode?.disconnect();
        this.audioBufferSourceNode?.disconnect();
        this.imSpeaking = false;
    }

    public async setOutputVolume(gain: number) {
        if (this.outputConfig.textOnly) return;

        if (!this.audioContext) {
            console.log("No audio context")
            return;
        }

        this.outputConfig.gain = gain;
        if (this.currentGainNode)
            this.currentGainNode.gain.setValueAtTime(this.outputConfig.gain, this.audioContext.currentTime);
    }

    public async setOutputPace(pace: number) {
        this.outputConfig.pace = pace;
    }

    public getOutputConfig(): Output {
        return this.outputConfig;
    }

    private buildSsml(textToSpeak: string,
                      lang: string,
                      voice: string,
                      pace: number) {
        const ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${lang}"><voice name="${voice}"><prosody rate="${pace}">${textToSpeak}</prosody></voice></speak>`;
        console.log('Build SSML: ', ssml);
        return ssml;
    }

    private playOnDevice(buffer: AudioBuffer) {
        if (this.outputConfig.textOnly) return;

        if (!this.audioContext) {
            console.log("No audio context")
            return;
        }

        this.imSpeaking = true;

        this.currentGainNode = this.audioContext.createGain();
        this.currentGainNode.gain.setValueAtTime(this.outputConfig.gain, this.audioContext.currentTime);

        this.audioBufferSourceNode = this.audioContext.createBufferSource();
        this.audioBufferSourceNode.buffer = buffer;
        this.audioBufferSourceNode.onended = (event) => {
            this.currentGainNode?.disconnect();
            this.audioBufferSourceNode?.disconnect();
            this.imSpeaking = false;
            //onSpeakComplete();
        }

        this.audioBufferSourceNode.connect(this.currentGainNode);
        this.currentGainNode.connect(this.audioContext.destination);
        console.log('[Text2SpeechProcessor] audioBufferSourceNode start');
        this.audioBufferSourceNode.start();
    }

    // public sendToDevice = (data: ArrayBuffer) => {
    //     this.audioContext.decodeAudioData(data, (buffer) => {
    //         this.sendToSpeakerQueue(buffer);
    //     }, error => {
    //         console.log('[Text2SpeechProcessor] err', error)
    //     });
    // }

    private speak(textToSpeak: string) {

        const sendToDevice = (data: ArrayBuffer) => {
            if (this.outputConfig.textOnly) return;

            if (!this.audioContext) {
                console.log("No audio context")
                return;
            }

            this.audioContext.decodeAudioData(data, (buffer) => {
                this.sendToSpeakerQueue(buffer);
            }, error => {
                console.log('[Text2SpeechProcessor] err', error)
            });
        }

        const ssml = this.buildSsml(textToSpeak, this.outputConfig.language.speakKey, this.outputConfig.language.voices[DEFAULT_VOICE_INDEX], this.outputConfig.pace);

        this.speechSynthesizer.speakSsmlAsync(ssml, (speechResult => {
            console.log('[Text2SpeechProcessor] Speech Ready', speechResult, speechResult.audioData);
            sendToDevice(speechResult.audioData);
        }), err => {
            console.log('[Text2SpeechProcessor] Speech error', err);
        });

        /*this.speechSynthesizer.speakTextAsync(textToSpeak, (speechResult => {
            console.log('[Text2SpeechProcessor] Speech Ready', speechResult, speechResult.audioData);
            playOnDevice(speechResult.audioData);
        }), err => {
            console.log('[Text2SpeechProcessor] Speech error', err);
        });*/
    }


    public processRecognition(modelType: string, partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {

        if ('onlyStableModel' !== modelType && 'ur' !== this.outputConfig.language.translationKey) {
            this.processAlphabeticLanguage(true, partiallyRecognizedText, onRecognitionReady);
            this.currentRecognizedTokens = [];
        } else {
            this.processLanguageWithoutPunctuation(true, partiallyRecognizedText, onRecognitionReady);
        }
    }

    public processLanguageWithoutPunctuation(isStable: boolean, recognizedText: string, onRecognitionReady: (recognizedText: string) => void) {
        console.log('About to say: [', recognizedText)
        onRecognitionReady(recognizedText);
    }

    public processAlphabeticLanguage(isStable: boolean, partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {

        if (!this.currentRecognizedTokens)
            this.currentRecognizedTokens = [];

        let length = this.currentRecognizedTokens.length;

        console.log('processAlphabeticLanguage 1', partiallyRecognizedText);

        const tokens = partiallyRecognizedText.match(/[^.;?!]+[.;?!]?/g);
        if (!tokens) return;
        console.log('processAlphabeticLanguage 1.b', tokens);
        let lastSentenceOfTheRecognizing = tokens[tokens.length - (isStable ? 1 : 2)];
        console.log('processAlphabeticLanguage 2', lastSentenceOfTheRecognizing);
        if (!lastSentenceOfTheRecognizing || (!isStable && !['.', ';', '?', '!', '、', '。', '।'].includes(lastSentenceOfTheRecognizing.charAt(lastSentenceOfTheRecognizing.length - 1))))
            return;

        let wordsToSay = '';

        console.log('processAlphabeticLanguage 3');

        //1. tokenizo por espacio, cuantas palabras en lastSaid? -> N
        //2. dame las N primeras palabras de lastSentences
        //3. aplico leihman entre los dos conjuntos
        //4. si paso el score de igualdad -> descarto N de lastSentenceOfTheRecognizing, y digo lo que queda y sumo eso al array de dichos
        //lo digo en su totalidad, lo sumo al array de dichos, sigo con mi vida
        if (this.currentRecognizedTokens.length === 0) {
            console.log('processAlphabeticLanguage 3.b first words ', lastSentenceOfTheRecognizing);
            wordsToSay = lastSentenceOfTheRecognizing;
        } else {

            let lastSaid = this.currentRecognizedTokens[this.currentRecognizedTokens.length - 1]; //1.
            let lastSaidWordCount = lastSaid.split(' ').length; //1.
            let firstWordsOfRecognizing = ''; //2.
            console.log('processAlphabeticLanguage 4');
            let lastSentenceOfTheRecognizingSplitted = lastSentenceOfTheRecognizing.split(' ');
            let limit = lastSaidWordCount <= lastSentenceOfTheRecognizingSplitted.length ? lastSaidWordCount : lastSentenceOfTheRecognizingSplitted.length
            for (let i = 0; i < (limit); i++) {
                firstWordsOfRecognizing = firstWordsOfRecognizing + lastSentenceOfTheRecognizingSplitted[i] + ' ';
            }
            firstWordsOfRecognizing = firstWordsOfRecognizing.trimEnd()
            console.log('processAlphabeticLanguage 5');

            console.log('Comparing: [', firstWordsOfRecognizing, '] vs [', lastSaid, ']')

            let similarityScore = levenshtein(firstWordsOfRecognizing, lastSaid); //3.
            console.log('Levenshtein score: ', similarityScore)


            if (similarityScore < 3) {//4.
                //similar phrases, avoid repeating first words
                if (lastSentenceOfTheRecognizingSplitted.length === lastSaidWordCount) {
                    //nothing to say? shouldn't happen
                    return;
                }

                for (let i = lastSaidWordCount; i < lastSentenceOfTheRecognizingSplitted.length; i++) {
                    wordsToSay = wordsToSay + lastSentenceOfTheRecognizingSplitted[i] + ' ';
                }
                wordsToSay = wordsToSay.trimEnd()
            } else {
                wordsToSay = lastSentenceOfTheRecognizing;
            }
        }

        console.log('About to say: [', wordsToSay)

        this.currentRecognizedTokens.push(wordsToSay);
        console.log('Pushing token: ', wordsToSay)
        onRecognitionReady(wordsToSay);
    }

    public processPartialRecognition(partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {
        this.processPartialLegacy(partiallyRecognizedText, onRecognitionReady);
    }

    public processPartialLegacy(partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {
        if (!this.currentRecognizedTokens)
            this.currentRecognizedTokens = [];

        let length = this.currentRecognizedTokens.length;

        //hola como estas? bien bien. soy pepe. y vos?
        //hola
        //hola como estas
        //hola como estas? bien
        //Hola como estás? bien bien
        //Hola como estás? bien bien. soy pepe
        //Hola como estás? bien bien. soy pepe. y

        //recognized
        //Hola como estás? Bien bien soy Pepe. y vos?

        //Leihman

        const tokens = partiallyRecognizedText.match(/[^.;?!]+[.;?!]?/g);

        if (!tokens) return;
        //hola. i 0 l 1
        //hola. dada i 0 i 1 l 2
        for (let i = 0; i < (tokens?.length || 0); i++) {
            const token = tokens[i];

            // Check if the token at this index has been processed before
            if (!this.currentRecognizedTokens[i]) {

                if (!['.', ';', '?', '!'].includes(token.charAt(token.length - 1))) return;
                // Process the token using translationProcessor
                //this.translationProcessor.enqueueText(token);
                // Add the token's index to the map
                this.currentRecognizedTokens.push(token);
                console.log('Pushing token: ', token)
                onRecognitionReady(token);
            }
        }
    }
}
